import sendMessageToParent, { isInApp } from "../helpers/parentMessageHandler";
import environment from "../constants/environment";
import { decryptData, encryptData } from "../helpers/encrypt-decrypt";
import storage from "../helpers/storage";
import { store } from "../redux/store";
import { POST_MESSAGE_EVENTS, STORAGE_KEYS } from "src/constants/common";

const methods = {
  POST: "post",
  GET: "get",
  PUT: "put",
  PATCH: "patch",
  DELETE: "delete",
};

interface RequestConfig {
  route: string;
  method: string;
  priv?: boolean;
  encrypt?: boolean;
  query_params?: Record<string, string | number>;
  payload?: any;
  headers?: Record<string, string>;
}

const handleAppLogout = () => {
  if (isInApp()) {
    sendMessageToParent(POST_MESSAGE_EVENTS.LOGOUT, {});
    return;
  }
};

const handleSessionExpired = () => {
  handleAppLogout();

  return {
    code: -1,
    message: "Session expired",
  };
};

const handleResponse = async (
  response: Response,
  encrypt: boolean = true,
): Promise<any> => {
  if (response.status === 401) {
    handleSessionExpired();
    return;
  }

  if (!response.ok) {
    const error = await response.text();
    throw new Error(
      `HTTP error! Status: ${response.status}, Message: ${error}`,
    );
  }

  let res: any;

  const contentType = response.headers.get("Content-Type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    res = await response.json();
  } else {
    res = await response.text();
  }

  if (encrypt) {
    const decryptedResponse = decryptData(res);
    const parsedResponse = JSON.parse(decryptedResponse);
    return parsedResponse;
  } else {
    return res;
  }
};

const request = async ({
  method,
  route,
  priv = true,
  encrypt = true,
  query_params = {},
  payload = null,
  headers = {},
}: RequestConfig): Promise<any> => {
  const state = store.getState();
  // const hc_type = state.auth.user.data?.hcType;

  // const headers: any = {
  //   ...(hc_type
  //     ? { health_secret: environment.API_HEALTH_SECRET }
  //     : { "api-key": environment.API_KEY }),
  //   ...(priv && { token: await getToken() }),
  // };

  const newHeaders: any = {
    "api-key": environment.API_KEY,
    "ngrok-skip-browser-warning": "true",
    ...(priv && { token: storage.getItem(STORAGE_KEYS.AUTH_TOKEN) }),
  };

  if (!newHeaders["token"] && priv) {
    storage.removeAll();
    window.location.reload();
    return;
  }

  const isFormData = payload instanceof FormData;

  const config: RequestInit = {
    method,
    headers: {
      ...newHeaders,
      ...headers,
    },
    body: isFormData
      ? payload
      : ["post", "put", "patch"].includes(method)
        ? encrypt && payload
          ? encryptData(payload)
          : payload
        : undefined,
  };

  const queryString = new URLSearchParams(
    Object.entries(query_params).reduce(
      (acc, [key, value]) => {
        acc[key] = String(value);
        return acc;
      },
      {} as Record<string, string>,
    ),
  ).toString();

  const baseUrl = environment.API_BASE_URL;
  // "https://02c1-2401-4900-bbe3-e8a0-e064-47f4-528a-16a.ngrok-free.app/api/v8"; //environment.API_BASE_URL;

  const basePath = `${baseUrl}${route}`;
  const url = queryString ? `${basePath}?${queryString}` : basePath;

  try {
    const response = await fetch(url, config);
    return await handleResponse(response, encrypt);
  } catch (err) {
    console.error("Request failed:", err);
    if (err instanceof Error && err.message === "unauthorized") {
      handleSessionExpired();
      return;
    }
    throw err;
  }
};

export { request, methods };
